import React, { useState } from "react";
import { ChevronDown, ChevronRight, ChevronLeft, Shield, Scale, FileText, Mail, Calendar, AlertTriangle, CheckCircle, Clock, Users, Zap } from "lucide-react";
import { motion } from 'framer-motion';
import { Link } from "react-router-dom";


const Terms = () => {
  const [expandedSections, setExpandedSections] = useState({});
  const [lastUpdated] = useState("September 15, 2025");

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      icon: CheckCircle,
      color: "text-green-600",
      content: (
        <div className="space-y-4">
          <p>
            By accessing, browsing, or using Tuly.app ("Service," "Platform," "we," "us," or "our"), 
            you acknowledge that you have read, understood, and agree to be bound by these Terms of Service 
            ("Terms") and our Privacy Policy.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <p className="text-blue-800">
              <strong>Important:</strong> If you do not agree with any part of these Terms, 
              you must discontinue use of our Service immediately.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "eligibility",
      title: "2. Eligibility and Account Requirements",
      icon: Users,
      color: "text-blue-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Age Requirements</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>You must be at least 13 years old to use Tuly.app</li>
            <li>Users under 18 must have parental/guardian consent</li>
            <li>Business accounts require authorized representative approval</li>
          </ul>
          
          <h4 className="font-semibold text-gray-900 mt-6">Account Responsibility</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Provide accurate, current, and complete registration information</li>
            <li>Maintain the security and confidentiality of your login credentials</li>
            <li>Notify us immediately of any unauthorized account access</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
            <p className="text-amber-800">
              <strong>Security Notice:</strong> We support OAuth login through Google and other providers. 
              Your account security also depends on the security of your connected accounts.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "service-description",
      title: "3. Service Description and Usage",
      icon: Zap,
      color: "text-purple-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">AI Workflow Automation Platform</h4>
          <p className="text-gray-700">
            Tuly.app provides AI-powered workflow automation services, including but not limited to:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-2">Core Services</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Automation Finder (AI Consultant)</li>
                <li>• Workflow generation and optimization</li>
                <li>• Tool recommendations and integrations</li>
                <li>• Budget estimation and ROI forecasting</li>
                <li>• Risk assessment and mitigation</li>
              </ul>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-2">Additional Features</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Executive summary generation</li>
                <li>• Implementation roadmaps</li>
                <li>• Performance analytics</li>
                <li>• Team collaboration tools</li>
                <li>• API access (premium plans)</li>
              </ul>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-6">Acceptable Use</h4>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h5 className="font-semibold text-red-800 mb-2">Prohibited Activities</h5>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Using the service for illegal, harmful, or fraudulent purposes</li>
              <li>• Attempting to reverse engineer or compromise our systems</li>
              <li>• Sharing account credentials or reselling access</li>
              <li>• Generating content that violates third-party rights</li>
              <li>• Excessive API calls or system abuse</li>
              <li>• Circumventing usage limits or security measures</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "subscription",
      title: "4. Subscription Plans and Billing",
      icon: FileText,
      color: "text-indigo-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Plan Types</h4>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <h5 className="font-semibold text-green-600">Free Plan</h5>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">
                <li>• Limited workflow generations</li>
                <li>• Basic features access</li>
                <li>• Community support</li>
              </ul>
            </div>
            <div className="border rounded-lg p-4">
              <h5 className="font-semibold text-blue-600">Pro Plan</h5>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">
                <li>• Unlimited workflows</li>
                <li>• Advanced AI features</li>
                <li>• Priority support</li>
                <li>• API access</li>
              </ul>
            </div>
            <div className="border rounded-lg p-4">
              <h5 className="font-semibold text-purple-600">Enterprise</h5>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">
                <li>• Custom integrations</li>
                <li>• Dedicated support</li>
                <li>• SLA guarantees</li>
                <li>• On-premise options</li>
              </ul>
            </div>
          </div>

          <h4 className="font-semibold text-gray-900 mt-6">Billing Terms</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Subscription fees are billed in advance on a monthly or annual basis</li>
            <li>All fees are non-refundable except as required by law</li>
            <li>Automatic renewal unless cancelled 24 hours before renewal date</li>
            <li>Price changes require 30 days advance notice</li>
            <li>Taxes may apply based on your location</li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <p className="text-blue-800">
              <strong>Cancellation:</strong> You may cancel your subscription at any time through 
              your account settings. Access continues until the end of your current billing period.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "data-ai",
      title: "5. Data Processing and AI Services",
      icon: Shield,
      color: "text-cyan-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Data Usage and AI Training</h4>
          <p className="text-gray-700">
            Our AI services process your input data to generate workflows, recommendations, and insights. 
            We are committed to responsible AI use and data protection.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-green-600 mb-3">What We Do</h5>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✓ Process data only as needed for service delivery</li>
                <li>✓ Implement industry-standard security measures</li>
                <li>✓ Provide data export and deletion options</li>
                <li>✓ Comply with GDPR, CCPA, and other privacy laws</li>
                <li>✓ Use anonymized data for service improvements</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-red-600 mb-3">What We Don't Do</h5>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✗ Share personal data with third parties for marketing</li>
                <li>✗ Use confidential business data in AI training</li>
                <li>✗ Store payment information on our servers</li>
                <li>✗ Access your data without explicit permission</li>
                <li>✗ Retain data longer than necessary</li>
              </ul>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h5 className="font-semibold text-purple-800 mb-2">AI Accuracy Disclaimer</h5>
            <p className="text-purple-700 text-sm">
              While our AI systems are designed to provide accurate and helpful recommendations, 
              AI-generated content should be reviewed and validated before implementation. 
              We do not guarantee the accuracy, completeness, or suitability of AI-generated workflows 
              for your specific use case.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "intellectual-property",
      title: "6. Intellectual Property Rights",
      icon: Scale,
      color: "text-orange-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Our Intellectual Property</h4>
          <p className="text-gray-700">
            Tuly.app, including all content, features, functionality, software, text, graphics, logos, 
            and trademarks, is owned by us or our licensors and protected by copyright, trademark, 
            and other intellectual property laws.
          </p>

          <h4 className="font-semibold text-gray-900 mt-6">Your Content</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>You retain ownership of content you submit to our platform</li>
            <li>You grant us a limited license to process your content for service delivery</li>
            <li>You represent that you have rights to all submitted content</li>
            <li>We may remove content that violates these Terms or applicable laws</li>
          </ul>

          <h4 className="font-semibold text-gray-900 mt-6">Generated Workflows</h4>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-700 text-sm">
              Workflows and recommendations generated by our AI become your property upon creation. 
              However, we retain the right to use anonymized patterns and insights derived from 
              generated content to improve our services, without revealing specific details 
              or attributing content to individual users.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "availability",
      title: "7. Service Availability and Support",
      icon: Clock,
      color: "text-teal-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Service Level</h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Uptime Target</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• 99.9% uptime for paid plans</li>
                <li>• 99.5% uptime for free plans</li>
                <li>• Scheduled maintenance windows</li>
                <li>• Status page for real-time updates</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Support Levels</h5>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• Community support (Free plan)</li>
                <li>• Email support (Pro plan)</li>
                <li>• Priority support (Enterprise)</li>
                <li>• 24/7 support (Enterprise SLA)</li>
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
            <p className="text-yellow-800">
              <strong>Service Interruptions:</strong> We will provide advance notice of planned 
              maintenance. Emergency maintenance may occur without notice to protect service security.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "liability",
      title: "8. Disclaimers and Limitation of Liability",
      icon: AlertTriangle,
      color: "text-red-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Service Disclaimers</h4>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm font-medium">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
          </div>

          <h4 className="font-semibold text-gray-900 mt-6">Limitation of Liability</h4>
          <p className="text-gray-700">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TULY.APP SHALL NOT BE LIABLE FOR:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 text-sm">
            <li>Indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
            <li>Any damages resulting from AI-generated recommendations</li>
            <li>Third-party actions or breaches of security</li>
            <li>Service interruptions or data loss</li>
          </ul>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <p className="text-blue-800 text-sm">
              <strong>Maximum Liability:</strong> Our total liability shall not exceed the amount 
              you paid for the service in the 12 months preceding the claim, or $100, whichever is greater.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "termination",
      title: "9. Termination and Account Closure",
      icon: FileText,
      color: "text-gray-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Termination by You</h4>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Cancel your subscription at any time through account settings</li>
            <li>Request account deletion by contacting support</li>
            <li>Export your data before account closure</li>
            <li>No refunds for partial billing periods</li>
          </ul>

          <h4 className="font-semibold text-gray-900 mt-6">Termination by Us</h4>
          <p className="text-gray-700 mb-2">We may terminate your account if you:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Violate these Terms of Service</li>
            <li>Engage in fraudulent or harmful activities</li>
            <li>Fail to pay subscription fees (after grace period)</li>
            <li>Abuse our systems or exceed usage limits</li>
          </ul>

          <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
            <p className="text-orange-800">
              <strong>Data Retention:</strong> After account termination, we may retain your data 
              for up to 30 days for recovery purposes, then permanently delete it unless required 
              by law to retain longer.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "governing-law",
      title: "10. Governing Law and Dispute Resolution",
      icon: Scale,
      color: "text-indigo-600",
      content: (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Jurisdiction</h4>
          <p className="text-gray-700">
            These Terms shall be interpreted and governed by the laws of [Insert Jurisdiction], 
            without regard to its conflict of law provisions.
          </p>

          <h4 className="font-semibold text-gray-900 mt-6">Dispute Resolution</h4>
          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-4">
              <h5 className="font-semibold text-blue-700">Step 1: Direct Resolution</h5>
              <p className="text-sm text-gray-700">Contact our support team to resolve issues informally</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h5 className="font-semibold text-green-700">Step 2: Mediation</h5>
              <p className="text-sm text-gray-700">Non-binding mediation for unresolved disputes</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4">
              <h5 className="font-semibold text-orange-700">Step 3: Arbitration</h5>
              <p className="text-sm text-gray-700">Binding arbitration as final resolution method</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-700 text-sm">
              <strong>Class Action Waiver:</strong> You agree to resolve disputes individually 
              and waive the right to participate in class actions or collective proceedings.
            </p>
          </div>
        </div>
      )
    }
  ];

   // Animation variants for the toggle button
  const toggleVariants = {
    open: { scale: 1 },
    closed: { scale: 1 },
    hover: { scale: 1.05 }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-4xl font-bold">Terms of Service</h1>
                <div className="flex items-center gap-4 mt-2 text-blue-100">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>Last updated: {lastUpdated}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>15 min read</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xl text-blue-100 leading-relaxed">
            These Terms of Service govern your access to and use of Tuly.app's AI-powered workflow automation platform. 
            Please read them carefully as they contain important information about your rights and obligations.
          </p>
        </div>
      </div>


      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Quick Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 p-2 text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <section.icon className={`w-4 h-4 ${section.color}`} />
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.id} id={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center ${section.color}`}>
                    <section.icon className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                </div>
                {expandedSections[section.id] ? 
                  <ChevronDown className="w-5 h-5 text-gray-400" /> : 
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                }
              </button>
              
              {(expandedSections[section.id] || section.id === 'acceptance') && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="pt-6">
                    {section.content}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-8 mt-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Questions About These Terms?</h3>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              If you have any questions about these Terms of Service or need clarification on any points, 
              our legal and support teams are here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:legal@tuly.app"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                legal@tuly.app
              </a>
              <a
                href="mailto:support@tuly.app"
                className="inline-flex items-center gap-2 bg-white text-blue-600 border border-blue-200 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                <Mail className="w-4 h-4" />
                support@tuly.app
              </a>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center text-sm text-gray-500 mt-8 p-4 bg-gray-100 rounded-lg">
          <p>
            These Terms of Service are effective as of {lastUpdated} and supersede all previous versions. 
            We reserve the right to modify these terms with 30 days notice to active users.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;